import { createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../api/axiosInstance';

/**
 * Fetch all employee targets the user has access to
 * GET /api/employee-targets/
 */
export const fetchTargetsThunk = createAsyncThunk(
  'employeeTargets/fetchAll',
  async ({ offset = 0, limit = 20 } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ limit, offset });
      const { data } = await axiosInstance.get(`/api/employee-targets/?${params}`);
      return data;
    } catch (err) {
      return rejectWithValue('Failed to fetch employee targets');
    }
  }
);

/**
 * Fetch targets for the current logged-in employee
 * GET /api/employee-targets/my-targets/
 */
export const fetchMyTargetsThunk = createAsyncThunk(
  'employeeTargets/fetchMine',
  async ({ activeOnly = false } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ active: activeOnly.toString() });
      const { data } = await axiosInstance.get(`/api/employee-targets/my-targets/?${params}`);
      return data;
    } catch (err) {
      return rejectWithValue('Failed to fetch your targets');
    }
  }
);

/**
 * Fetch a single target by ID
 * GET /api/employee-targets/:id/
 */
export const fetchTargetThunk = createAsyncThunk(
  'employeeTargets/fetchOne',
  async (targetId, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get(`/api/employee-targets/${targetId}/`);
      return data;
    } catch (err) {
      return rejectWithValue('Failed to fetch target');
    }
  }
);

/**
 * Fetch detailed progress for a target
 * GET /api/employee-targets/:id/progress-details/
 */
export const fetchTargetProgressThunk = createAsyncThunk(
  'employeeTargets/fetchProgress',
  async (targetId, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get(`/api/employee-targets/${targetId}/progress-details/`);
      return data;
    } catch (err) {
      return rejectWithValue('Failed to fetch target progress');
    }
  }
);

/**
 * Create a new employee target (admin only)
 * POST /api/employee-targets/
 */
export const createTargetThunk = createAsyncThunk(
  'employeeTargets/create',
  async (targetData, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post('/api/employee-targets/', targetData);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Failed to create target');
    }
  }
);

/**
 * Update an employee target (admin only)
 * PATCH /api/employee-targets/:id/
 */
export const updateTargetThunk = createAsyncThunk(
  'employeeTargets/update',
  async ({ targetId, updates }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.patch(`/api/employee-targets/${targetId}/`, updates);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Failed to update target');
    }
  }
);

/**
 * Delete an employee target (admin only)
 * DELETE /api/employee-targets/:id/
 */
export const deleteTargetThunk = createAsyncThunk(
  'employeeTargets/delete',
  async (targetId, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/api/employee-targets/${targetId}/`);
      return targetId;
    } catch (err) {
      return rejectWithValue('Failed to delete target');
    }
  }
);

/**
 * Fetch target summary statistics (admin or team leader only)
 * GET /api/employee-targets/summary/
 */
export const fetchTargetSummaryThunk = createAsyncThunk(
  'employeeTargets/fetchSummary',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get('/api/employee-targets/summary/');
      return data;
    } catch (err) {
      return rejectWithValue('Failed to fetch target summary');
    }
  }
);